import SubHeader from "@/components/SubHeader";
import LottieAnimation from "@/components/Lottie";
import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";

/**
 * @param {import("@prismicio/client").Content.ProjectCardSliceDefaultPrimaryCardsItem | import("@prismicio/client").Content.ProjectCardSliceTextPrimaryCardsItem} card
 * @param {string} [className]
 */
const ChallengeCard = ({ card, animationSrc, index }) => {
  if (!isFilled.richText(card.title) && !isFilled.richText(card.text)) {
    return null;
  }

  return (
    <div className={`flex flex-row gap-[30px] rounded-[10px] bg-beige-50 px-[30px] py-10 ${animationSrc ? "w-full" : "w-full lg:w-1/2"}`}>
      {animationSrc && (
        <div className="w-[148px] h-[148px] hidden md:block">
          <LottieAnimation
            src={animationSrc}
            className="h-full"
          />
        </div>
      )}
      <div className="flex flex-col gap-[28px] flex-1">
        <div className="flex flex-row gap-6 items-center">
          <span
            className="flex text-number-guide text-bleu bg-beige w-[48px] h-[48px] rounded-[10px] items-center justify-center"
            aria-hidden
          >
            {index + 1}
          </span>
          {isFilled.richText(card.title) && (
            <PrismicRichText field={card.title} components={{
              heading3: ({ children }) => <h3 className="p-lg-n sbd flex-1">{children}</h3>,
              heading4: ({ children }) => <h4 className="p-lg-n sbd flex-1">{children}</h4>,
            }} />
          )}
        </div>


        {isFilled.richText(card.text) && (
          <PrismicRichText field={card.text} components={{
            paragraph: ({ children }) => (
              <p className="p-lg-n text-bleu">
                {children}
              </p>
            ),
          }} />
        )}
      </div>
    </div>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ProjectCardSlice} ProjectCardSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProjectCardSlice>} ProjectCardProps
 * @type {import("react").FC<ProjectCardProps>}
 */
const ProjectCard = ({ slice }) => {
  const footerText =
    slice.variation === "text" ? slice.primary.text : undefined;
  const { sub_header, title, cards } = slice.primary;
  const filledCards = Array.isArray(cards)
    ? cards
      .filter(
        (card) =>
          isFilled.richText(card.title) || isFilled.richText(card.text),
      )
      .slice(0, 3)
    : [];
  const firstCard = filledCards.shift();

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30">
      <div className="flex flex-col gap-10">

        {(isFilled.keyText(sub_header) || isFilled.richText(title)) && (
          <div className="w-full">
            <div className={`framing w-full flex flex-col gap-[40px]`}>
              <div className="flex flex-col gap-[25px]">
                <div className="flex flex-col gap-3">
                  {isFilled.keyText(sub_header) && <SubHeader text={sub_header} />}
                  {isFilled.richText(title) && (
                    <PrismicRichText
                      field={title}
                      components={{
                        heading2: ({ children }) => (
                          <h2 className="h2-n dk bd text-bleu">{children}</h2>
                        ),
                        heading3: ({ children }) => (
                          <h3 className="h2-n dk bd text-bleu">{children}</h3>
                        ),
                        heading4: ({ children }) => (
                          <h4 className="h2-n dk bd text-bleu">{children}</h4>
                        )
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {filledCards.length > 0 && (
          <div className="flex flex-col gap-[30px] overflow-x-auto">
            <ChallengeCard
              key={`card-mobile-0`}
              index={0}
              card={firstCard}
              animationSrc="https://lottie.host/545ace02-42cd-4a7a-9a76-510c7a7d5909/ds2LgksoYA.lottie"
            />
            <div className="flex flex-col md:flex-row gap-[30px]">
              {filledCards.map((card, index) => (
                <ChallengeCard
                  index={index + 1}
                  key={`card-mobile-${index}`}
                  card={card}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section >
  );
};

export default ProjectCard;
